import React, {useRef} from 'react';
import PropTypes from 'prop-types';
import css from './Table.module.scss';
import {TableProvider, useTableContext} from './TableContext';
import TableHead from './TableHead';
import TableRow from './TableRow';

const getNodeText = (node) => {
  if (typeof node === 'string' || typeof node === 'number') {
    return String(node);
  }
  if (Array.isArray(node)) {
    return node.map(getNodeText).join('');
  }
  if (React.isValidElement(node) && node.props?.children !== undefined) {
    return getNodeText(node.props.children);
  }
  return '';
};

const collectHeaderLabels = (children) => {
  const labels = [];
  const head = React.Children.toArray(children).find(
    (child) => React.isValidElement(child) && child.type === TableHead,
  );
  if (!head) {
    return labels;
  }
  React.Children.toArray(head.props.children).forEach((headChild) => {
    if (!React.isValidElement(headChild)) {
      return;
    }
    if (headChild.type === TableRow) {
      React.Children.toArray(headChild.props.children).forEach((headerCell) => {
        if (React.isValidElement(headerCell)) {
          const colSpan = parseInt(headerCell.props.colSpan, 10) || 1;
          const label = getNodeText(headerCell.props.children).trim();
          for (let i = 0; i < colSpan; i++) {
            labels.push(label);
          }
        }
      });
    } else {
      const colSpan = parseInt(headChild.props.colSpan, 10) || 1;
      const label = getNodeText(headChild.props.children).trim();
      for (let i = 0; i < colSpan; i++) {
        labels.push(label);
      }
    }
  });
  return labels;
};

const TableInner = ({children, className, cellAlign, cellVerticalAlign, compact, style, columnIds, fixedColumnWidths, tableRef, mobileSections}) => {
  const {columnWidths} = useTableContext();

  const hasColumnIds = columnIds && columnIds.length > 0;
  const resolveWidth = (id) => {
    if (fixedColumnWidths && fixedColumnWidths[id] !== undefined) {
      return fixedColumnWidths[id];
    }
    return columnWidths[id];
  };
  const allMeasured = hasColumnIds && columnIds.every(id => resolveWidth(id) !== undefined);

  return (
    <table
      ref={tableRef}
      className={`${css.table} ${css[`align-${cellAlign}`] || ''} ${css[`valign-${cellVerticalAlign}`] || ''} ${compact ? css.compact : ''} ${hasColumnIds ? css.tableResizable : ''} ${allMeasured ? css.tableFixed : ''} ${mobileSections ? css.tableSections : ''} ${className}`.trim()}
      style={style}
    >
      {allMeasured &&
        <colgroup>
          {columnIds.map(id => (
            <col key={id} style={{width: `${resolveWidth(id)}px`}} />
          ))}
        </colgroup>
      }
      {children}
    </table>
  );
};

const Table = ({
  children,
  className = '',
  cellAlign = 'left',
  cellVerticalAlign = 'middle',
  style,
  scrollable = false,
  mobileLayout = 'sections',
  compact = false,
  storageKey,
  columnIds,
  fixedColumnWidths,
}) => {
  const tableRef = useRef(null);

  const mobileSections = mobileLayout === 'sections' && !scrollable;
  const headerLabels = mobileSections ? collectHeaderLabels(children) : [];

  const tableElement = (
    <TableProvider
      cellAlign={cellAlign}
      storageKey={storageKey}
      columnIds={columnIds}
      fixedColumnWidths={fixedColumnWidths}
      tableRef={tableRef}
      headerLabels={headerLabels}
      mobileSections={mobileSections}
    >
      <TableInner
        className={className}
        cellAlign={cellAlign}
        cellVerticalAlign={cellVerticalAlign}
        compact={compact}
        style={style}
        columnIds={columnIds}
        fixedColumnWidths={fixedColumnWidths}
        tableRef={tableRef}
        mobileSections={mobileSections}
      >
        {children}
      </TableInner>
    </TableProvider>
  );

  if (scrollable || mobileLayout === 'scroll') {
    return (
      <div className={css.scrollableTableWrapper}>
        {tableElement}
      </div>
    );
  }

  return tableElement;
};

Table.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  cellAlign: PropTypes.oneOf(['left', 'center', 'right']),
  cellVerticalAlign: PropTypes.oneOf(['top', 'middle', 'bottom']),
  style: PropTypes.object,
  scrollable: PropTypes.bool,
  mobileLayout: PropTypes.oneOf(['sections', 'scroll']),
  compact: PropTypes.bool,
  storageKey: PropTypes.string,
  columnIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  fixedColumnWidths: PropTypes.object,
};

TableInner.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  cellAlign: PropTypes.oneOf(['left', 'center', 'right']),
  cellVerticalAlign: PropTypes.oneOf(['top', 'middle', 'bottom']),
  compact: PropTypes.bool,
  style: PropTypes.object,
  columnIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  fixedColumnWidths: PropTypes.object,
  tableRef: PropTypes.shape({current: PropTypes.any}),
  mobileSections: PropTypes.bool,
};

export default Table;
